import { Component, output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

/**
 * BirthdateInputFuseComponent
 * Version moderne du composant legacy birthdate-input
 * Pattern: Standalone, OnPush, inject(), takeUntilDestroyed()
 */
@Component({
  selector: 'app-birthdate-input-fuse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    TranslateModule,
  ],
  providers: [
    DatePipe,
    provideMomentDateAdapter({
      parse: {
        dateInput: 'DD/MM/yyyy',
      },
      display: {
        dateInput: 'DD/MM/yyyy',
        monthYearLabel: 'MMMM YYYY',
        dateA11yLabel: 'DD/MM/yyyy',
        monthYearA11yLabel: 'MMMM yyyy',
      }
    }),
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  template: `
    <mat-form-field class="birthdate-field w-full">
      <input 
        class="birthdate-input cursor-pointer" 
        matInput 
        [matDatepicker]="picker" 
        (click)="picker.open()" 
        [formControl]="birthdate" 
        [max]="maxDate">
      <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
      <mat-datepicker 
        #picker 
        startView="multi-year" 
        [disabled]="false" 
        (opened)="disableInput()" 
        (closed)="enableInput()">
      </mat-datepicker>
    </mat-form-field>
    <mat-error *ngIf="birthdate.touched && birthdate.invalid" translate>
      missing.information.form.birthday.error
    </mat-error>
  `,
  styles: [`
    .birthdate-field {
      width: 100%;
    }
    
    .birthdate-input {
      cursor: pointer;
    }
  `]
})
export class BirthdateInputFuseComponent {
  private destroyRef = inject(DestroyRef);
  private adapter = inject(DateAdapter);
  private datePipe = inject(DatePipe);

  // Output
  birthdateChange = output<string>();

  // Form Control
  birthdate = new FormControl('', Validators.required);
  maxDate: Date = new Date();

  constructor() {
    this.adapter.setLocale('fr');

    // Écouter les changements de date
    this.birthdate.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onDateChange());
  }

  private onDateChange(): void {
    const formattedDate = this.datePipe.transform(this.birthdate.value, 'yyyy-MM-dd');
    this.birthdate.setValue(formattedDate, { emitEvent: false });
    this.birthdateChange.emit(formattedDate);
  }

  disableInput(): void {
    this.birthdate.disable();
  }

  enableInput(): void {
    this.birthdate.enable();
  }
}
